/**
 * The invitation acts both the list and the record perform, and what every act
 * refreshes afterwards. Each page decides where the reader goes after an act;
 * the request, the refresh, and the sentence the reader is told live here once.
 */
import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { useAuth } from "@/contexts/AuthContext";
import { useTenantPresentation } from "@/hooks/use-tenant-presentation";
import { ApiError } from "@/lib/api/client";
import { invitationsApi, type Invitation, type IssuedInvitation } from "@/lib/api/invitations";
import { PEOPLE_QUERY_KEY } from "@/lib/api/people";
import { tenantSlugFromHostname } from "@/lib/tenant";
import type { TenantSlug } from "@/lib/theme-presets";

import {
  deliveryAccepted,
  deliveryProblem,
  type SetupEmailReissueRequest,
} from "./invitation-presentation";

const MANAGED_ACCOUNT_DOMAINS: Readonly<Record<TenantSlug, string>> = {
  murtada: "murtadaacademy.org",
  mufid: "mufidseminary.org",
};

const MANAGED_WORKSPACE_NAMES: Readonly<Record<TenantSlug, string>> = {
  murtada: "Murtada Workspace",
  mufid: "Mufid Workspace",
};

/**
 * The managed Workspace this session may compose addresses in: only on the
 * signed-in tenant's own trusted host, otherwise none.
 */
export function useManagedWorkspace(): { managedDomain: string | null; workspaceName: string | null } {
  const { me } = useAuth();
  const hostTenant = tenantSlugFromHostname(window.location.hostname);
  const managedDomain = hostTenant && hostTenant === me?.tenant_slug
    ? MANAGED_ACCOUNT_DOMAINS[hostTenant]
    : null;
  const workspaceName = managedDomain && me?.tenant_slug
    ? MANAGED_WORKSPACE_NAMES[me.tenant_slug as TenantSlug]
    : null;
  return { managedDomain, workspaceName };
}

/** Every surface an invitation transition can change reads again. */
export function useInvitationRefresh(): () => void {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: PEOPLE_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: ["dashboard", "admin"] });
    void queryClient.invalidateQueries({ queryKey: ["invitations"] });
    void queryClient.invalidateQueries({ queryKey: ["post-admission-readiness"] });
    void queryClient.invalidateQueries({ queryKey: ["invitation-events"] });
    void queryClient.invalidateQueries({ queryKey: ["managed-identity-resolution-queue"] });
    void queryClient.invalidateQueries({ queryKey: ["existing-workspace-admin-handoffs"] });
  }, [queryClient]);
}

/** A fresh setup or activation email, or the reissue of a revoked invitation. */
export function useResendInvitation(
  onSent?: (receipt: IssuedInvitation, source: SetupEmailReissueRequest) => void,
) {
  const { name: tenantName } = useTenantPresentation();
  const refresh = useInvitationRefresh();
  return useMutation({
    mutationFn: (request: SetupEmailReissueRequest) => invitationsApi.resend(
      request.id,
      request.deliveryEmail,
    ),
    onSuccess: (res, source) => {
      onSent?.(res, source);
      refresh();
      if (deliveryAccepted(res)) {
        toast.success(`A fresh ${tenantName}-branded setup email was accepted for delivery.`);
      } else if (res.delivery_state === "ambiguous") {
        toast.error("The email provider outcome is unknown. Send a fresh setup email before handoff.");
      } else {
        toast.error(`The fresh setup email was not accepted for delivery. ${deliveryProblem(res.delivery_state)}`);
      }
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Could not resend the invitation"),
  });
}

/** The fixed bootstrap invitation's own containment-safe reissue. */
export function useResendBootstrapInvitation(
  onSent?: (receipt: IssuedInvitation, source: Invitation) => void,
) {
  const refresh = useInvitationRefresh();
  return useMutation({
    mutationFn: (invitation: Invitation) => invitationsApi.resendBootstrap(invitation.id),
    onSuccess: (res, source) => {
      onSent?.(res, source);
      refresh();
      toast.success(
        res.delivery_state === "sent"
          ? "A new secure bootstrap link was sent. The previous link no longer works."
          : `A new secure bootstrap link was created but not delivered (${res.delivery_state}).`,
      );
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Could not reissue the bootstrap invitation"),
  });
}
